import _ from "lodash";
import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";

import { Button, EmptyState } from "@toddle-edu/ds-web";
import { NoSearchInFilterIllustration } from "@toddle-edu/ds-theme";
import { WarningCircleOutlined } from "@toddle-edu/ds-icons";

import { graphql, compose } from "lib/apolloClient";

import { getWorkbookTemplateFolderQuery } from "../../modules/WorkbookTemplateQueries";
import { getWorkbookTemplateFolderQueryV2 } from "../../modules/WorkbookTemplateQueriesV2";
import { getWorkbookTemplatesFolderFromCache } from "../../modules/WorkbookTemplateGraphqlHelpers";
import { isNewTemplateResolverEnabled } from "../../modules/Utils";

import { getUserId } from "modules/CommonSelectors";
import { getIsAdminByRole } from "lib/staffPermissionUtils";

import { useHandleScroll } from "Utils/CustomHooks";
import { getFormattedUserName } from "Utils/GetFormattedUserName";

import { prepareLoadMoreFeed } from "Utils";

import I18nHOC from "UIComponents/I18nHOC";
import Loading from "UIComponents/Loading";
import PaginationLoader from "UIComponents/PaginationLoader";
import WorkbookTemplateCard from "AppComponents/Workbook/WorkbookTemplateCard";

import classes from "./CarouselCardsV2.scss";
import classNames from "classnames";

const styles = {
  titleStyle: {
    fontSize: "16px",
  },
  subtitleStyle: {
    fontSize: "14px",
  },
};

const CarouselCardsV2 = props => {
  const {
    userId,
    template,
    templateData,
    updateFolderId,
    folderId,
    id,
    t,
    isLoading,
    isData,
    loadMoreEvents,
    hasNextPage,
    networkStatus,
    scrollRef,
    isFetchingMoreData,
    onPreviewTemplateClick,
    onUseTemplateClick,
    onEditTemplateClick,
    onDeleteTemplateClick,
    folderLabel,
    isAdmin,
  } = props;

  const [numberOfCardsToRender, setNumberOfCardsToRender] = useState();
  const [showPaginationLoader, setShowPaginationLoader] = useState(false);

  const getNumberOfCardsToRender = useCallback(() => {
    const bodyContainer = scrollRef.current;
    const bodyContainerWidth = bodyContainer?.offsetWidth - 64; // Get the width of the body container and subtract padding
    const numberOfCardsToRender = Math.floor(bodyContainerWidth / 244); // 244 is the width of the card
    setNumberOfCardsToRender(numberOfCardsToRender);
  }, [scrollRef]);

  const debouncedGetNumberOfCardsToRender = useCallback(
    _.debounce(getNumberOfCardsToRender, 100),
    [getNumberOfCardsToRender]
  );

  useEffect(() => {
    window.addEventListener("resize", debouncedGetNumberOfCardsToRender);
    getNumberOfCardsToRender();

    return () => {
      window.removeEventListener("resize", debouncedGetNumberOfCardsToRender);
      debouncedGetNumberOfCardsToRender.cancel();
    };
  }, [debouncedGetNumberOfCardsToRender, getNumberOfCardsToRender]);

  const getScrollRef = () => {
    return scrollRef.current;
  };

  useHandleScroll({
    networkStatus: networkStatus,
    loadMoreFeed: loadMoreEvents,
    hasNextPage,
    getScrollRef,
  });

  useEffect(() => {
    setShowPaginationLoader?.(isFetchingMoreData);
  }, [setShowPaginationLoader, isFetchingMoreData]);

  let label = _.get(template, "label", "");
  let templates = _.get(template, "childrenV2.edges", []);

  const isFolderSelected = !_.isEqual(folderId, "allTemplates");
  const isSchoolTemplateSelected = _.isEqual(folderId, "schoolTemplate");
  const templateId = _.get(template, "id", "");
  const isSchoolTemplates = _.isEqual(templateId, "schoolTemplate");

  if (isFolderSelected) {
    templates = _.get(templateData, "childrenV2.edges", []);
    label = folderLabel;
  }

  const isTemplateAvailable = !_.isEmpty(templates);

  const seperatorContainerClass = classNames({
    [classes.seperatorContainer]: !isFolderSelected,
    [classes.selectedCategoryContainer]: isFolderSelected,
  });
  const containerStyle = classNames({
    [classes.container]: isFolderSelected,
  });
  const noSchoolTemplatesLabelStyle = classNames(
    "text-body-2",
    classes.noSchoolTemplatesLabel
  );

  const renderTemplateCard = () => {
    let templateArr;

    if (!isFolderSelected) {
      templateArr = _.filter(
        _.slice(templates, 0, numberOfCardsToRender),
        item => {
          return item?.node;
        }
      );
    } else {
      templateArr = templates;
    }

    const nodes = _.map(templateArr, item => {
      return item?.node;
    });

    return _.map(nodes, node => {
      const { label, id, attachment, createdBy } = node;
      const metadata = _.get(attachment, "metadata", {});
      const url = _.get(attachment, "url", "");
      const thumbUrl = _.get(attachment, "thumbUrl", "");
      const numOfPages = _.get(metadata, "numOfPages");

      const isTemplateCreator =
        (isSchoolTemplateSelected || isSchoolTemplates) &&
        (_.isEqual(userId, _.get(createdBy, "id")) || isAdmin);

      const creator = getFormattedUserName({ user: createdBy, type: "staff" });

      return (
        <WorkbookTemplateCard
          key={id}
          id={id}
          attachment={attachment}
          url={url}
          thumbUrl={thumbUrl}
          label={label}
          creator={creator}
          numOfPages={numOfPages}
          isTemplateCreator={isTemplateCreator}
          onUseTemplateClick={onUseTemplateClick}
          onPreviewTemplateClick={onPreviewTemplateClick}
          onEditTemplateClick={onEditTemplateClick}
          onDeleteTemplateClick={onDeleteTemplateClick}
        />
      );
    });
  };

  const renderHeading = () =>
    (isTemplateAvailable || isFolderSelected || isSchoolTemplates) &&
    !isSchoolTemplateSelected && (
      <div className={classes.headingContainer}>
        <h6>{label}</h6>
        {_.get(templates, "length") > numberOfCardsToRender &&
          !isFolderSelected && (
            <Button
              dsVersion={"2.0"}
              variant={"neutral"}
              type={"outlined"}
              key={id}
              onClick={() => updateFolderId(id)}
              size={"small"}
            >
              {t("community:see_all")}
            </Button>
          )}
      </div>
    );

  const noDataComponent = () => {
    return (
      isFolderSelected && (
        <div className={classes.noDataContainer}>
          {isLoading && !isData ? (
            <Loading />
          ) : (
            <EmptyState
              dsVersion={"2.0"}
              illustration={NoSearchInFilterIllustration}
              title={t("common:noResultHeading")}
              subtitle={t("workbook:no_templates_matching_your_search")}
              titleStyle={styles.titleStyle}
              subtitleStyle={styles.subtitleStyle}
            />
          )}
        </div>
      )
    );
  };

  const renderNoTemplatesAvailable = () =>
    isSchoolTemplates ? (
      <div className={classes.noSchoolTemplatesContainer}>
        <WarningCircleOutlined variant="subtle" size="xxx-small" />
        <div className={noSchoolTemplatesLabelStyle}>
          {t("workbook:no_school_templates_created")}
        </div>
      </div>
    ) : (
      noDataComponent()
    );

  const renderData = () =>
    isTemplateAvailable ? (
      <>
        <div
          className={seperatorContainerClass}
          style={{
            gridTemplateColumns: `repeat(${numberOfCardsToRender}, minmax(0, 1fr))`,
          }}
        >
          {renderTemplateCard()}
        </div>
        {showPaginationLoader ? <PaginationLoader /> : null}
      </>
    ) : (
      renderNoTemplatesAvailable()
    );

  return (
    <div className={containerStyle}>
      {renderHeading()}
      {renderData()}
    </div>
  );
};

const templateFolderFilters = {
  types: "ORGANIZATION_RESOURCE",
  portalType: "RESOURCE_LIBRARY",
  first: 24,
  states: ["PUBLISHED"],
};

const templateFolderFiltersV2 = {
  types: "WORKBOOK_TEMPLATE_RESOURCE",
  portalType: "RESOURCE_LIBRARY",
  first: 24,
  states: ["PUBLISHED"],
};

const mapStateToProps = (state, ownProps) => {
  const folderId = _.get(ownProps, "folderId", "");
  const searchText = _.get(ownProps, "searchText", "");
  const isData = _.get(ownProps, "isData", true);
  const isLoading = _.get(ownProps, "isLoading", false);

  return {
    userId: getUserId(state),
    isAdmin: getIsAdminByRole(),
    folderId,
    isLoading,
    isData,
    templateFolderFilters: {
      ...(isNewTemplateResolverEnabled()
        ? templateFolderFiltersV2
        : templateFolderFilters),
      id: folderId,
      searchText,
    },
  };
};

export default compose(
  I18nHOC({ resource: ["common"] }),
  connect(mapStateToProps),
  graphql(getWorkbookTemplateFolderQuery, {
    name: "getWorkbookTemplateFolder",
    skip: ({ folderId }) => {
      return (
        isNewTemplateResolverEnabled() ||
        _.isEqual(folderId, "allTemplates") ||
        _.isEqual(folderId, "schoolTemplate")
      );
    },
    options: ({ templateFolderFilters }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        ...templateFolderFilters,
      },
    }),
    props({
      getWorkbookTemplateFolder,
      ownProps: { isData, isLoading, templateFolderFilters },
    }) {
      const data = getWorkbookTemplatesFolderFromCache({
        ...templateFolderFilters,
      });
      const templateData = _.get(data, "node", []);
      const endCursor = _.get(
        templateData,
        "childrenV2.pageInfo.endCursor",
        ""
      );
      const networkStatus = _.get(getWorkbookTemplateFolder, "networkStatus");
      return {
        onPusherEventTriggered: () => getWorkbookTemplateFolder.refetch(),
        templateData,
        networkStatus,
        isData: !_.isEmpty(templateData) && isData,
        isFetchingMoreData: _.isEqual(networkStatus, 3),
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        hasNextPage: _.get(
          templateData,
          "childrenV2.pageInfo.hasNextPage",
          false
        ),
        loadMoreEvents: prepareLoadMoreFeed({
          variables: {
            ...templateFolderFilters,
            after: endCursor,
          },
          queryData: getWorkbookTemplateFolder,
          key: "childrenV2",
        }),
      };
    },
  }),

  graphql(getWorkbookTemplateFolderQueryV2, {
    name: "getWorkbookTemplateFolderV2",
    skip: ({ folderId }) => {
      return (
        !isNewTemplateResolverEnabled() ||
        _.isEqual(folderId, "allTemplates") ||
        _.isEqual(folderId, "schoolTemplate")
      );
    },
    options: ({ templateFolderFilters }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        ...templateFolderFilters,
      },
    }),
    props({
      getWorkbookTemplateFolderV2,
      ownProps: { isData, isLoading, templateFolderFilters },
    }) {
      const data = getWorkbookTemplatesFolderFromCache({
        ...templateFolderFilters,
      });
      const templateData = _.get(data, "node", []);
      const endCursor = _.get(
        templateData,
        "childrenV2.pageInfo.endCursor",
        ""
      );
      const networkStatus = _.get(getWorkbookTemplateFolderV2, "networkStatus");
      return {
        onPusherEventTriggered: () => getWorkbookTemplateFolderV2.refetch(),
        templateData,
        networkStatus,
        isData: !_.isEmpty(templateData) && isData,
        isFetchingMoreData: _.isEqual(networkStatus, 3),
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        hasNextPage: _.get(
          templateData,
          "childrenV2.pageInfo.hasNextPage",
          false
        ),
        loadMoreEvents: prepareLoadMoreFeed({
          variables: {
            ...templateFolderFilters,
            after: endCursor,
          },
          queryData: getWorkbookTemplateFolderV2,
          key: "childrenV2",
        }),
      };
    },
  })
)(CarouselCardsV2);
